/* eslint-disable max-len,react/no-unescaped-entities */
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  Avatar, Box, Container, Divider, Tooltip, Typography, Link as BackLink,
} from '@mui/material';
import personImg from 'assets/images/person.svg';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import { Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import './style.scss';
import { GridColDef } from '@mui/x-data-grid';
import { OutlineArrowCircleLeftIcon } from 'assets/icons';
import { ExpertHubLayout } from 'components/templates';
import DOMPurify from 'isomorphic-dompurify';
import { getAvatarLetters, getUserName } from 'utils/common-utils';
import {
  CustomTable, Loader, WhiteBgCard,
} from '../../../components';
import { SessionBookedItemData } from '../../sessions/interface';
import { selectProfile, selectUsersLoader } from '../selector';
import { usersAction } from '../slice';

const itemTableColumns: GridColDef[] = [
  {
    field: 'title',
    headerName: 'Name',
    flex: 0.5,
    renderCell: (params) => (
      <Tooltip title={params.value} enterDelay={1000}>
        <Typography variant="body2" component="p">
          {params.value}
        </Typography>
      </Tooltip>
    ),
  },
  {
    field: 'sessionType',
    headerName: 'Type',
    flex: 0.25,
  },
];

const EndUserProfileView = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const loading = useSelector(selectUsersLoader);
  const profile = useSelector(selectProfile);

  const [data, setData] = React.useState<SessionBookedItemData[]>([]);
  const [page, setPage] = React.useState(0);

  const greyBoxStyle = {
    border: 1,
    borderColor: 'divider',
    backgroundColor: 'background.default',
    borderRadius: 1.5,
    padding: 2,
  };

  React.useEffect(() => {
    dispatch(usersAction.fetchUserProfile(id as string));
  }, [dispatch, id]);

  return (
    <ExpertHubLayout sx={{}}>
      <Loader loading={loading}>
        <Container sx={{ pb: 6, mt: 1 }}>
          <BackLink href="/users" component="a" underline="none">
            <Typography
              component="a"
              display="flex"
              alignItems="center"
              gap={2}
              color="primary.main"
              sx={{ cursor: 'pointer', textDecoration: 'none' }}
            >
              <OutlineArrowCircleLeftIcon
                sx={{ path: { stroke: (t) => t.palette.primary.main } }}
              />
              Back to users
            </Typography>
          </BackLink>
        </Container>
        <div className="container px-0">
          <div className="row">
            <div className="col-lg-8">
              <WhiteBgCard sx={{ mb: 3 }}>
                <Avatar
                  sx={{ width: 80, height: 80, mb: 2 }}
                  alt={getAvatarLetters(profile)}
                  src={profile?.profileImgUrl}
                >
                  {getAvatarLetters(profile)}
                </Avatar>

                <Typography variant="h5" sx={{ color: 'text.primary', mb: 1 }}>
                  {getUserName(profile)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(profile?.bio && profile.bio.length > 0 ? profile.bio : '') }} />

                <div className="row mt-4">
                  <div className="col-12 col-sm-6 col-md-7">
                    <Typography variant="body2" sx={{ color: 'grey.900', mb: 0.5 }}>
                      Location
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {profile.country}
                    </Typography>
                  </div>
                </div>

                <Divider sx={{ mt: 2, mb: 4 }} />

                <div className="row">
                  <div className="col-12 col-sm-6 col-md-5 row mx-0">
                    <div className="col-auto">
                      <img src={personImg} alt="person" />
                    </div>
                    <div className="col mt-2">

                      <div className="mb-4">
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary', lineHeight: 1, mb: 1.1 }}
                        >
                          Height
                        </Typography>
                        <Typography variant="h3" sx={{ color: 'text.primary', lineHeight: 1 }}>
                          {`${profile?.attributes?.height ?? 0} m`}
                        </Typography>
                      </div>

                      <div className="mb-4">
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary', lineHeight: 1, mb: 1.1 }}
                        >
                          Weight
                        </Typography>
                        <Typography variant="h3" sx={{ color: 'text.primary', lineHeight: 1 }}>
                          {`${profile?.attributes?.weight ?? 0} kg`}
                        </Typography>
                      </div>

                      <div className="mb-4">
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary', lineHeight: 1, mb: 1.1 }}
                        >
                          BMI kg/m²
                        </Typography>
                        <Typography variant="h3" sx={{ color: 'text.primary', lineHeight: 1 }}>
                          {profile?.attributes?.bmi ?? 0}
                        </Typography>
                      </div>

                    </div>
                  </div>
                  <div className="col-12 col-sm-6 col-md-7">
                    <Typography variant="h6" sx={{ color: 'text.primary', mb: 3 }}>
                      Badges and certifications
                    </Typography>
                    <List sx={{ width: '100%', py: 0 }}>
                      {profile.attributes.badges.map(({ badgeTitle, badgeImgUrl, badgeDescription }: any) => (
                        <ListItem sx={{ p: 0, mb: 2 }}>
                          <ListItemAvatar>
                            <Avatar
                              alt={badgeTitle}
                              src={badgeImgUrl}
                              sx={{ mr: 1 }}
                            />
                          </ListItemAvatar>
                          <ListItemText
                            sx={{ my: 0 }}
                            primary={<Typography variant="body2">{badgeTitle}</Typography>}
                            secondary={badgeDescription}
                          />
                        </ListItem>
                      ))}
                      {profile.attributes.badges.length === 0 && (
                      <Typography variant="body2" sx={{ my: 2, color: 'text.secondary' }}>No badges assigned</Typography>
                      )}
                    </List>

                  </div>

                </div>
              </WhiteBgCard>

              <WhiteBgCard>
                <Typography variant="h6" sx={{ mb: 3 }}>
                  Sessions purchased
                </Typography>

                <CustomTable
                  rows={profile.attributes.sessionsPurchased}
                  columns={itemTableColumns}
                  rowIdProperty="_id"
                  disableSelectionOnClick
                  loading={loading}
                  hideFooter={false}
                  pagination
                  page={page}
                  setPage={setPage}
                  rowsPerPageOptions={5}
                  pageSize={5}
                  rowCount={profile.attributes.sessionsPurchased.length}
                />

              </WhiteBgCard>
            </div>
            <div className="col-lg-4">
              <WhiteBgCard sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Purchases made
                </Typography>

                <div className="row">
                  <div className="col-6">
                    <Box sx={[greyBoxStyle]}>
                      <Typography variant="body2">
                        Sessions
                      </Typography>
                      <Typography variant="h4" sx={{ mt: 1, mb: 0.5, color: 'text.primary' }}>
                        {profile.attributes.sessionsPurchased.length}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        All time
                      </Typography>
                    </Box>
                  </div>
                  <div className="col-6">
                    <Box sx={[greyBoxStyle]}>
                      <Typography variant="body2">
                        Products
                      </Typography>
                      <Typography variant="h4" sx={{ mt: 1, mb: 0.5, color: 'text.primary' }}>
                        0
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        All time
                      </Typography>
                    </Box>
                  </div>
                </div>

              </WhiteBgCard>

              <WhiteBgCard>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Reviews given
                </Typography>

                <Swiper
                  modules={[Pagination]}
                  spaceBetween={50}
                  slidesPerView={1}
                  pagination={{
                    el: '.custom-swiper-pagination',
                    clickable: true,
                    renderBullet(index, className) {
                      return `<span class="${className}"></span>`;
                    },
                  }}
                  onSwiper={(swiper) => { }}
                  onSlideChange={() => { }}
                  className="mySwiper mb-4"
                  loop
                >

                  {profile.reviews.map((review) => (
                    <SwiperSlide>
                      <Box sx={{
                        borderRadius: 1.5, backgroundColor: 'grey.A100', p: 2, mb: 2,
                      }}
                      >
                        <Typography variant="body2">
                          {review.review}
                        </Typography>
                      </Box>

                      <ListItem sx={{ p: 0 }}>
                        <ListItemAvatar>
                          <Avatar
                            alt={getAvatarLetters(review?.expertId)}
                            src={review?.expertId?.profileImgUrl}
                            sx={{ mr: 1 }}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          sx={{ my: 0 }}
                          primary={(
                            <Typography variant="body2" sx={{ color: 'text.primary' }}>
                              {getUserName(review?.expertId)}
                            </Typography>
                        )}
                          secondary=""
                        />
                      </ListItem>
                    </SwiperSlide>
                  ))}

                </Swiper>
                <div className="custom-swiper-pagination d-flex justify-content-center align-items-center text-center" />

              </WhiteBgCard>
            </div>
          </div>
        </div>
      </Loader>
    </ExpertHubLayout>
  );
};

export default EndUserProfileView;
